import type { Config } from '@puckeditor/core';
import { RichTextEditor } from '../../components/ui/RichTextEditor';
import { MediaPicker } from '../../components/ui/MediaPicker';
import { LinkPicker } from '../../components/ui/LinkPicker';
import { OverlayField } from './OverlayField';
import { TitleCardField } from './TitleCardField';
import AiTextInput from './AiTextInput';
import CollectionKeyField from './CollectionKeyField';
import { BgSwatchField, BgColorField, ColorField } from './BackgroundField';
import { PxSizeField } from './SizeField';
import type { BlockDef, BlockFieldDef } from '@palacete/shared/blocks';
import { BLOCK_CATALOG } from '@palacete/shared/blocks';

function aiText(label: string, blockType: string, fieldName: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return {
    type: 'custom',
    label,
    isAiText: true,
    render: ({ value, onChange }: { value?: string; onChange: (v: string) => void }) => (
      <AiTextInput value={value ?? ''} onChange={onChange} blockType={blockType} fieldName={fieldName} />
    ),
  } as any;
}

const BLOCK_STYLES: Record<string, { color: string; bg: string }> = {
  Hero: { color: '#818cf8', bg: '#312e81' },
  PropertiesGrid: { color: '#2dd4bf', bg: '#134e4a' },
  Agents: { color: '#fb923c', bg: '#7c2d12' },
  TextSection: { color: '#c084fc', bg: '#581c87' },
  Stats: { color: '#facc15', bg: '#713f12' },
  Contact: { color: '#4ade80', bg: '#14532d' },
  CTA: { color: '#f87171', bg: '#7f1d1d' },
  Blog: { color: '#60a5fa', bg: '#1e3a8a' },
  Testimonials: { color: '#f472b6', bg: '#831843' },
  StickyScroll: { color: '#a78bfa', bg: '#4c1d95' },
  FAQ: { color: '#34d399', bg: '#064e3b' },
  Offices: { color: '#38bdf8', bg: '#0c4a6e' },
  AppointmentBooking: { color: '#f59e0b', bg: '#78350f' },
  GoogleReviews: { color: '#fbbf24', bg: '#713f12' },
  LinkInBio: { color: '#f0abfc', bg: '#701a75' },
  InstagramHighlights: { color: '#fb7185', bg: '#881337' },
  CollectionSection: { color: '#5eead4', bg: '#134e4a' },
  Gallery: { color: '#a3e635', bg: '#365314' },
  LogoStrip: { color: '#fcd34d', bg: '#78350f' },
  Pricing: { color: '#22d3ee', bg: '#155e75' },
  Features: { color: '#c4b5fd', bg: '#4c1d95' },
  Video: { color: '#f87171', bg: '#7f1d1d' },
  Divider: { color: '#94a3b8', bg: '#334155' },
  Search: { color: '#7dd3fc', bg: '#075985' },
  Checklist: { color: '#4ade80', bg: '#14532d' },
  Comparison: { color: '#a78bfa', bg: '#4c1d95' },
  SocialLinks: { color: '#f0abfc', bg: '#701a75' },
  PricingCalculator: { color: '#22d3ee', bg: '#155e75' },
};

function Preview({ type, summary }: { type: string; summary?: string }) {
  const style = BLOCK_STYLES[type] ?? { color: '#94a3b8', bg: '#1e293b' };

  return (
    <div style={{
      padding: '24px',
      border: `2px solid ${style.color}`,
      borderRadius: '12px',
      background: '#111827',
      boxShadow: `0 4px 20px ${style.color}22`,
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      margin: '8px 0',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: 900,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#ffffff',
          background: style.color,
          padding: '4px 12px',
          borderRadius: '20px',
        }}>
          {type}
        </span>
      </div>
      {summary ? (
        <div style={{ color: '#f3f4f6', fontSize: '0.9375rem', fontWeight: 600, lineHeight: '1.4' }}>
          {summary}
        </div>
      ) : (
        <div style={{ color: '#9ca3af', fontSize: '0.8125rem', fontStyle: 'italic' }}>
          Sin titular configurado
        </div>
      )}
      <div style={{ color: '#6b7280', fontSize: '0.75rem', letterSpacing: '0.04em' }}>
        Abre <strong style={{ color: style.color }}>Vista en vivo</strong> para ver este bloque renderizado de verdad →
      </div>
    </div>
  );
}

// ─── Customization framework helpers ─────────────────────────────────────────

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Derecha' },
];
const PAD_OPTIONS = [
  { value: 'none', label: 'Sin espaciado' },
  { value: 'compact', label: 'Compacto' },
  { value: 'regular', label: 'Normal' },
  { value: 'spacious', label: 'Amplio' },
];
const WIDTH_OPTIONS = [
  { value: 'contained', label: 'Contenido' },
  { value: 'wide', label: 'Ancho' },
  { value: 'full', label: 'Completo' },
];
const ANIM_OPTIONS = [
  { value: 'reveal', label: 'Revelar' },
  { value: 'fade', label: 'Desvanecer' },
  { value: 'rise', label: 'Subir' },
  { value: 'none', label: 'Sin animación' },
];
const ANIM_SPEED_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'slow', label: 'Lenta' },
  { value: 'fast', label: 'Rápida' },
];
const TONE_OPTIONS = [
  { value: '', label: 'Automático (según el color)' },
  { value: 'light', label: 'Fondo claro (texto oscuro)' },
  { value: 'dark', label: 'Fondo oscuro (texto claro)' },
];

function sizeField(label: string) {
  return {
    type: 'custom', label,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render: (({ value, onChange }: any) => <PxSizeField value={value} onChange={onChange} />) as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
}

function designGroupField(shell: BlockDef['shell']) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const objectFields: Record<string, any> = {};
  if (shell.includes('bg')) {
    // Fondo: swatches de rol (A) + color personalizado con tono (B). El renderer
    // lo consume vía el contrato de superficie; bgColor/tone solo aplican cuando
    // el rol es "Personalizado".
    objectFields.bg = {
      type: 'custom', label: 'Fondo',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (({ value, onChange }: any) => <BgSwatchField value={value} onChange={onChange} />) as any,
    };
    objectFields.bgColor = {
      type: 'custom', label: 'Color personalizado',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (({ value, onChange }: any) => <BgColorField value={value} onChange={onChange} />) as any,
    };
    objectFields.tone = { type: 'select', label: 'Tono del color personalizado', options: TONE_OPTIONS };
    // Overrides de color por bloque (texto, botones, tarjetas). Opcionales: sin
    // color elegido, el bloque hereda el tema. El renderer deriva el contraste
    // del compañero (texto sobre botón/tarjeta) automáticamente.
    objectFields.inkColor = {
      type: 'custom', label: 'Color del texto',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (({ value, onChange }: any) => <ColorField value={value} onChange={onChange} />) as any,
    };
    objectFields.accentColor = {
      type: 'custom', label: 'Color de botones',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (({ value, onChange }: any) => <ColorField value={value} onChange={onChange} />) as any,
    };
    objectFields.cardColor = {
      type: 'custom', label: 'Color de tarjetas',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      render: (({ value, onChange }: any) => <ColorField value={value} onChange={onChange} />) as any,
    };
  }
  if (shell.includes('pad'))   objectFields.pad   = { type: 'select', label: 'Espaciado',  options: PAD_OPTIONS };
  if (shell.includes('width')) objectFields.width = { type: 'select', label: 'Anchura',    options: WIDTH_OPTIONS };
  if (shell.includes('align')) objectFields.align = { type: 'select', label: 'Alineación', options: ALIGN_OPTIONS };
  if (shell.includes('anim')) {
    objectFields.anim = { type: 'select', label: 'Animación', options: ANIM_OPTIONS };
    objectFields.animSpeed = { type: 'select', label: 'Velocidad', options: ANIM_SPEED_OPTIONS };
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return { type: 'object', label: 'Diseño', objectFields } as any;
}

// ─── Catalog → Puck field conversion ─────────────────────────────────────────

function fieldToPuck(field: BlockFieldDef, blockType: string): [string, any] {
  switch (field.type) {
    case 'text':
      return [field.key, field.aiText
        ? aiText(field.label, blockType, field.key)
        : { type: 'text', label: field.label }
      ];
    case 'textarea':
      return [field.key, { type: 'textarea', label: field.label }];
    case 'number':
      return [field.key, { type: 'number', label: field.label }];
    case 'richtext':
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <RichTextEditor value={value} onChange={onChange} />
        )) as any,
      }];
    case 'media': {
      // Solo los campos marcados 'auto'/'manual' en el contrato ofrecen generación IA.
      const aiImage = (field.generationPolicy && field.generationPolicy !== 'off')
        ? {
            blockType,
            fieldKey: field.key,
            fieldLabel: field.label,
            mediaRole: field.mediaRole,
            aspectRatio: field.aspectRatio,
            promptHints: field.promptHints,
          }
        : undefined;
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <MediaPicker
            value={value ? [value] : []}
            onChange={(urls: string[]) => onChange(urls[0])}
            multiple={field.multiple ?? false}
            folder={(field.folder ?? 'pages') as any}
            aiImage={aiImage}
          />
        )) as any,
      }];
    }
    case 'link':
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <LinkPicker value={value} onChange={onChange} />
        )) as any,
      }];
    case 'select':
      return [field.key, { type: 'select', label: field.label, options: field.options ?? [] }];
    case 'radio':
      return [field.key, { type: 'radio', label: field.label, options: field.options ?? [] }];
    case 'size':
      return [field.key, sizeField(field.label)];
    case 'overlay':
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <OverlayField value={value} onChange={onChange} />
        )) as any,
      }];
    case 'titlecard':
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <TitleCardField value={value} onChange={onChange} />
        )) as any,
      }];
    case 'collection-key':
      return [field.key, {
        type: 'custom', label: field.label,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        render: (({ value, onChange }: any) => (
          <CollectionKeyField value={(value as string) ?? ''} onChange={onChange} />
        )) as any,
      }];
    case 'array':
      return [field.key, {
        type: 'array',
        label: field.label,
        arrayFields: Object.fromEntries(
          (field.arrayFields ?? []).map(f => fieldToPuck(f, blockType))
        ),
      }];
    default:
      return [field.key, { type: 'text', label: field.label }];
  }
}

// ─── Per-block overrides (complex renders + resolveFields) ────────────────────

const COMPONENT_PATCHES: Record<string, Partial<Config['components'][string]>> = {
  Hero: {
    render: ({ heroStyle, headline }: any) => (
      <Preview type="Hero" summary={`${heroStyle} — "${headline}"`} />
    ),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolveFields: ((data: any, { fields }: any) => {
      const style = data.props?.heroStyle ?? 'fullscreen';
      const hasImage = Boolean(data.props?.backgroundImage);
      const showOverlay = style === 'fullscreen' || (style === 'minimal' && hasImage);
      const showCard = style === 'fullscreen';
      return Object.fromEntries(
        Object.entries(fields).filter(([key]) => {
          if (key === 'overlay') return showOverlay;
          if (key === 'titleCard') return showCard;
          return true;
        })
      );
    }) as any,
  },
  PropertiesGrid: {
    render: ({ featuredLayout, limit }: any) => (
      <Preview type="PropertiesGrid" summary={`Distribución: ${featuredLayout} · Máximo: ${limit}`} />
    ),
  },
  Stats: {
    render: ({ variant }: any) => (
      <Preview type="Stats" summary={`Variante: ${variant}`} />
    ),
  },
  Blog: {
    render: ({ limit }: any) => (
      <Preview type="Blog" summary={`Últimos ${limit} artículos`} />
    ),
  },
  LinkInBio: {
    render: ({ name }: any) => (
      <Preview type="LinkInBio" summary={(name as string) || 'Página de enlaces'} />
    ),
  },
  InstagramHighlights: {
    render: ({ title }: any) => (
      <Preview type="InstagramHighlights" summary={(title as string) || 'Destacados de Instagram'} />
    ),
  },
  CollectionSection: {
    render: ({ title, collectionKey }: any) => (
      <Preview type="CollectionSection" summary={(title as string) || (collectionKey as string) || 'Sección de contenido'} />
    ),
  },
};

// ─── Component builder ────────────────────────────────────────────────────────

function blockDefToComponent(def: BlockDef): any {
  const fields = Object.fromEntries(def.fields.map(f => fieldToPuck(f, def.type)));
  if (def.shell.length > 0) fields.design = designGroupField(def.shell);
  const base: any = {
    label: def.label,
    fields,
    defaultProps: def.defaults,
    render: (props: Record<string, unknown>) => (
      <Preview
        type={def.type}
        summary={def.summaryField ? (props[def.summaryField] as string) : undefined}
      />
    ),
  };
  const patch = COMPONENT_PATCHES[def.type];
  return patch ? { ...base, ...patch } : base;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const puckConfig: Config = {
  components: Object.fromEntries(
    BLOCK_CATALOG.map(def => [def.type, blockDefToComponent(def)])
  ) as Config['components'],
};
